import { useMemo } from "react"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/cn"
import type { OverTimeBucket } from "@/types/analytics_escalations"

// Stacked-bar view of escalations over time, broken down by reason.
// Two display modes:
//   "count"   — y-axis is raw escalation count
//   "percent" — y-axis is 0-100, where each day's stack height is
//               (total_escalations / total_convos) * 100, split
//               proportionally across categories.

type Props = {
  buckets: OverTimeBucket[]
  mode: "count" | "percent"
  onModeChange: (mode: "count" | "percent") => void
}

// Same palette as the donut so colors match across the two charts.
const PALETTE = [
  "#E0455B", "#D4A24C", "#22c55e", "#eab308",
  "#ef4444", "#0ea5e9", "#8b5cf6", "#64748b",
]

type Row = { date: string; total: number } & Record<string, number | string>

export function EscalationOverTimeChart({ buckets, mode, onModeChange }: Props) {
  const { rows, categoryKeys } = useMemo(() => {
    const keys = new Set<string>()
    for (const b of buckets) {
      for (const k of Object.keys(b.by_category)) keys.add(k)
    }
    const categoryKeys = Array.from(keys)

    const rows: Row[] = buckets.map((b) => {
      const total = b.total_escalations
      const escRate = b.total_convos > 0 ? (total / b.total_convos) * 100 : 0
      const row: Row = { date: b.date, total }
      for (const k of categoryKeys) {
        const c = b.by_category[k] ?? 0
        if (mode === "percent") {
          // each category's share of the day's escalation-rate
          row[k] = total > 0 ? (c / total) * escRate : 0
        } else {
          row[k] = c
        }
      }
      return row
    })
    return { rows, categoryKeys }
  }, [buckets, mode])

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Over time</h2>
        <ModeToggle mode={mode} onChange={onModeChange} />
      </div>
      {buckets.length === 0 ? (
        <p className="text-sm text-app-muted">No data in window.</p>
      ) : (
        <div className="h-[280px] min-w-0">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="date"
                stroke="#888"
                tick={{ fontSize: 11 }}
                tickFormatter={(d: string) => fmtShortDate(d)}
              />
              <YAxis
                stroke="#888"
                tick={{ fontSize: 11 }}
                tickFormatter={(v: number) => (mode === "percent" ? `${Math.round(v)}%` : `${v}`)}
                domain={mode === "percent" ? [0, "auto"] : ["auto", "auto"]}
              />
              <Tooltip
                contentStyle={{ background: "#1f1f23", border: "1px solid #333", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#aaa" }}
                itemStyle={{ color: "#fff" }}
                formatter={(v) => {
                  const n = typeof v === "number" ? v : Number(v)
                  return mode === "percent" ? `${n.toFixed(2)}%` : n.toLocaleString()
                }}
                labelFormatter={(d) => fmtShortDate(String(d))}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {categoryKeys.map((k, idx) => (
                <Bar
                  key={k}
                  dataKey={k}
                  stackId="a"
                  fill={PALETTE[idx % PALETTE.length]}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  )
}

function ModeToggle({ mode, onChange }: { mode: "count" | "percent"; onChange: (m: "count" | "percent") => void }) {
  return (
    <div className="inline-flex overflow-hidden rounded-control border border-border bg-white/5 text-xs">
      <button
        type="button"
        onClick={() => onChange("count")}
        className={cn(
          "px-3 py-1.5 transition",
          mode === "count" ? "bg-brand-red text-white" : "text-app-muted hover:text-app-text",
        )}
      >
        Count
      </button>
      <button
        type="button"
        onClick={() => onChange("percent")}
        className={cn(
          "px-3 py-1.5 transition",
          mode === "percent" ? "bg-brand-red text-white" : "text-app-muted hover:text-app-text",
        )}
      >
        %
      </button>
    </div>
  )
}

function fmtShortDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}
